import React, { useState, useEffect } from 'react';
import { ViewTab } from '../types';
import { PublicHeader } from '../components/layout';
import { WhatsAppIcon } from '../components/ui/WhatsAppIcon';
import { X, CheckCircle2, Loader2, TrendingUp, ShieldCheck, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '../auth/AuthContext';
import { api } from '../services/api';

interface PartnershipViewProps {
  onNavigate: (tab: ViewTab | string) => void;
}

export const PartnershipView: React.FC<PartnershipViewProps> = ({ onNavigate }) => {
  const { user } = useAuth() as any;
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    channelLink: '',
    phone: '',
    message: ''
  });

  useEffect(() => {
    if (user?.email) {
      setFormData(prev => ({
        ...prev,
        email: user.email,
        name: prev.name || user.fullName || (user.email ? user.email.split('@')[0] : ''),
        phone: prev.phone || user.phone || ''
      }));
    }
  }, [user]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  const openModal = () => {
    setIsApplyModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.channelLink) return;

    setIsSubmitting(true);
    setSubmitError(null);

    const fullMessage = formData.message
      ? `[Candidature Partenaire Officiel (80% M1 + 25% M2-6)] ${formData.message}`
      : `[Candidature Partenaire Officiel (80% M1 + 25% M2-6)]`;

    try {
      await api.partnership.apply({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        channelLinks: formData.channelLink,
        message: fullMessage
      });

      try {
        const existing = JSON.parse(localStorage.getItem('zeyilo_partner_leads') || '[]');
        existing.push({
          ...formData,
          submittedAt: new Date().toISOString()
        });
        localStorage.setItem('zeyilo_partner_leads', JSON.stringify(existing));
      } catch {}

      setIsSubmitted(true);
    } catch (err: any) {
      console.error('[Partnership] Erreur enregistrement:', err);
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setIsApplyModalOpen(false);
    setIsSubmitted(false);
    setSubmitError(null);
    setFormData({
      name: user?.fullName || (user?.email ? user.email.split('@')[0] : '') || '',
      email: user?.email || '',
      channelLink: '',
      phone: user?.phone || '',
      message: ''
    });
  };

  return (
    <div className="w-full bg-white text-slate-900 min-h-screen relative overflow-x-hidden flex flex-col justify-between">
      <PublicHeader onNavigate={onNavigate} activeTab="partenariat" />

      <main className="pt-24 sm:pt-28 pb-14 w-full flex-1 relative z-10">
        <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10 sm:space-y-12">

          {/* ── EN-TÊTE DU PROGRAMME PARTENAIRE ── */}
          <div className="max-w-3xl space-y-4">
            <h1 className="font-serif text-3xl sm:text-5xl lg:text-6xl font-normal text-slate-950 tracking-[-0.02em] leading-tight">
              Touchez 80 % dès le 1er mois, puis une rente mensuelle pendant 6 mois.
            </h1>
            <p className="text-sm sm:text-base text-slate-600 leading-relaxed max-w-2xl font-normal">
              Recommandez l'assistant IA WhatsApp conçu pour les commerces, PME et agences en Afrique francophone. Touchez <strong>80 % de commission dès le premier paiement</strong> de chaque entreprise parrainée, suivi de <strong>25 % récurrents pendant 5 mois</strong> (6 mois de commissions au total par client). Paiements Mobile Money sous 3 jours (72h) sans seuil minimum.
            </p>

            <div className="pt-2 flex flex-wrap gap-3 items-center">
              <button
                type="button"
                onClick={openModal}
                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 sm:py-3 rounded-full bg-slate-900 hover:bg-black text-white font-medium text-xs sm:text-sm transition-all shadow-xs cursor-pointer group"
              >
                <span>Rejoindre le Programme Partenaire</span>
                <span className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center text-[10px] text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform">↗</span>
              </button>
              <a
                href={`https://wa.me/2290129647182?text=${encodeURIComponent("Bonjour Zeyilo, je souhaite rejoindre le programme partenaire (80% le 1er mois + 25% récurrent pendant 6 mois). Voici ma communauté :")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 sm:py-3 rounded-full bg-white hover:bg-slate-50 text-slate-900 font-medium text-xs sm:text-sm transition-colors border border-slate-200 shadow-2xs"
              >
                <WhatsAppIcon className="w-4 h-4 text-[#25D366]" />
                <span>Discuter sur WhatsApp</span>
              </a>
            </div>
          </div>

          {/* ── LA FORMULE UNIQUE DU PARTENARIAT (100% FLAT) ── */}
          <div className="rounded-3xl bg-slate-100/80 p-6 sm:p-10 flex flex-col justify-between space-y-7 border-0 shadow-none">
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-white text-emerald-600 flex items-center justify-center border-0 shadow-none shrink-0">
                  <TrendingUp className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="font-serif text-xl sm:text-2xl font-normal text-slate-950 tracking-[-0.01em]">
                    Partenaire Officiel Zeyilo
                  </h2>
                  <p className="text-xs text-slate-500">
                    Modèle d'affiliation sur 6 mois
                  </p>
                </div>
              </div>

              <div>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-4xl sm:text-5xl font-black text-slate-950">80 % + 25 %</span>
                </div>
                <p className="text-sm font-bold text-emerald-700 mt-1.5">
                  80 % sur le 1er mois + 25 % récurrents pendant 5 mois supplémentaires (6 mois au total)
                </p>
                <p className="text-xs sm:text-sm text-slate-600 mt-2 max-w-2xl leading-relaxed">
                  Une formule unique, juste et hautement rémunératrice pour tous : créateurs de contenu, consultants, gestionnaires de communauté, agences et commerçants prescripteurs.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-3.5 pt-1">
                <div className="p-4 rounded-2xl bg-white border-0 shadow-none space-y-1.5">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span className="text-xs font-bold text-slate-900">80 % de commission au Mois 1</span>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed pl-6">
                    Encaissez <strong>80 % dès le 1er mois</strong> sur le premier abonnement souscrit par chaque entreprise que vous recommandez.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white border-0 shadow-none space-y-1.5">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span className="text-xs font-bold text-slate-900">25 % de rente (Mois 2 à 6)</span>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed pl-6">
                    Touchez <strong>25 % chaque mois</strong> pendant 5 mois supplémentaires sur chaque renouvellement mensuel de vos commerçants affiliés.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white border-0 shadow-none space-y-1.5">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span className="text-xs font-bold text-slate-900">Paiements Mobile Money sous 3 jours</span>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed pl-6">
                    Reversements directs via Wave, Orange Money, MTN MoMo ou Moov Money <strong>après 3 jours (72h de validation anti-fraude), sans seuil minimum de retrait</strong>.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white border-0 shadow-none space-y-1.5">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span className="text-xs font-bold text-slate-900">Forfait Pro Zeyilo & Tableau de bord</span>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed pl-6">
                    Accès Pro offert pour vos démonstrations + lien personnalisé <code>/p/VOTRECODE</code> et suivi en direct de vos gains.
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-1">
              <button
                type="button"
                onClick={openModal}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm transition-all cursor-pointer border-0 shadow-none"
              >
                <span>Rejoindre le Programme Partenaire Officiel</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* ── SECTION TRANSPARENCE & CONFIANCE : POURQUOI 6 MOIS ? ── */}
          <div className="rounded-3xl bg-emerald-50/60 p-6 sm:p-8 space-y-3 border-0 shadow-none">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white text-emerald-600 flex items-center justify-center shrink-0 border-0 shadow-none">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-base sm:text-lg font-bold text-slate-950">
                  Pourquoi une rémunération plafonnée à 6 mois par commerçant ?
                </h4>
                <p className="text-xs text-slate-600">
                  Une totale transparence entre partenaires, sans promesse trompeuse.
                </p>
              </div>
            </div>
            <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
              Contrairement aux programmes promettant des commissions « à vie » souvent intenables dans la durée, Zeyilo applique un modèle sain qui garantit le paiement rapide et systématique de chaque partenaire : nous vous reversons <strong>80 % du chiffre d'affaires dès le premier mois</strong> pour vous rémunérer tout de suite de votre recommandation, puis une rente mensuelle de <strong>25 % pendant 5 mois</strong> pour encourager l'accompagnement de vos commerçants. À partir du 7ème mois, le commerçant est autonome et pérenne sur la plateforme.
            </p>
          </div>

          {/* ── GARANTIE & PAIEMENTS MOBILE MONEY ── */}
          <div className="rounded-2xl bg-slate-100/80 border-0 shadow-none p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start sm:items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white text-emerald-600 flex items-center justify-center shrink-0 border-0 shadow-none">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div className="text-xs sm:text-sm">
                <p className="font-bold text-slate-950">Paiements garantis par Mobile Money</p>
                <p className="text-slate-600 mt-0.5">
                  Reversements directs via <strong>Wave, Orange Money, MTN MoMo & Moov Money</strong> dès validation des commissions • Tableau de bord en temps réel.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={openModal}
              className="w-full sm:w-auto shrink-0 px-5 py-2.5 rounded-xl bg-slate-950 hover:bg-slate-800 text-white font-semibold text-xs sm:text-sm transition-colors cursor-pointer border-0 shadow-none"
            >
              Candidater maintenant (1 min)
            </button>
          </div>

        </div>
      </main>

      {/* ── FOOTER ── */}
      <footer className="bg-white mt-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-7 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-2.5">
            <img src="/logo.webp" alt="Zeyilo" width={120} height={36} loading="lazy" decoding="async" className="h-5 w-auto object-contain shrink-0" />
            <span>— L'Agent IA WhatsApp des entreprises modernes</span>
          </div>

          <nav className="flex flex-wrap items-center justify-center gap-5">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                onNavigate('landing');
              }}
              className="text-slate-600 hover:text-slate-900 transition-colors"
            >
              Accueil
            </a>
            <a
              href="/register"
              onClick={(e) => {
                e.preventDefault();
                onNavigate('register');
              }}
              className="text-slate-600 hover:text-slate-900 transition-colors"
            >
              Inscription
            </a>
            <a
              href="/pricing"
              onClick={(e) => {
                e.preventDefault();
                onNavigate('pricing');
              }}
              className="text-slate-600 hover:text-slate-900 transition-colors"
            >
              Tarifs
            </a>
            <a href="/blog" className="text-slate-600 hover:text-slate-900 transition-colors">
              Blog
            </a>
            <a href="/confidentialite" className="text-slate-600 hover:text-slate-900 transition-colors">
              Confidentialité
            </a>
            <a href="/cgu" className="text-slate-600 hover:text-slate-900 transition-colors">
              CGU
            </a>
          </nav>
        </div>
      </footer>

      {/* ── MODALE DE CANDIDATURE PARTENAIRE ── */}
      <AnimatePresence>
        {isApplyModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/50 backdrop-blur-xs">
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.15 }}
              className="bg-white rounded-2xl border-0 shadow-none max-w-md w-full p-6 relative max-h-[90vh] overflow-y-auto"
            >
              <button
                type="button"
                onClick={handleCloseModal}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 p-1 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>

              {!isSubmitted ? (
                <div className="space-y-4">
                  <div className="space-y-1">
                    <h3 className="text-base font-bold text-slate-950">
                      Rejoindre le Programme Partenaire Officiel
                    </h3>
                    <p className="text-xs text-slate-500">
                      Remplissez ces informations pour recevoir votre lien affilié (80 % Mois 1 + 25 % Mois 2 à 6) et activer votre compte Pro.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Nom ou pseudo *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Ex : Alex K. ou Business Afrique"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-100/80 border-0 text-xs text-slate-900 focus:outline-none focus:bg-slate-100"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Numéro WhatsApp (pour activer vos accès & Mobile Money) *
                      </label>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="Ex : +225 07... / +221 77... / +229..."
                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-100/80 border-0 text-xs text-slate-900 focus:outline-none focus:bg-slate-100"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Lien chaîne / page (TikTok, YouTube, Facebook, Insta) *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.channelLink}
                        onChange={(e) => setFormData({ ...formData, channelLink: e.target.value })}
                        placeholder="Ex : tiktok.com/@votrecompte ou youtube.com/@..."
                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-100/80 border-0 text-xs text-slate-900 focus:outline-none focus:bg-slate-100"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="votre@email.com"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-100/80 border-0 text-xs text-slate-900 focus:outline-none focus:bg-slate-100"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Message ou précisions (optionnel)
                      </label>
                      <textarea
                        rows={2}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Parlez-nous brièvement de votre audience..."
                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-100/80 border-0 text-xs text-slate-900 focus:outline-none focus:bg-slate-100 resize-none"
                      />
                    </div>

                    {submitError && (
                      <p className="text-xs text-rose-600 bg-rose-50 p-2 rounded-lg">
                        {submitError}
                      </p>
                    )}

                    <div className="pt-1">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-slate-950 hover:bg-black text-white font-semibold text-xs transition-all cursor-pointer disabled:opacity-60"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            <span>Envoi en cours...</span>
                          </>
                        ) : (
                          <span>Valider ma candidature</span>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                <div className="text-center py-5 space-y-3">
                  <div className="w-11 h-11 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <h4 className="text-base font-bold text-slate-950">
                    Candidature reçue avec succès !
                  </h4>
                  <p className="text-xs text-slate-600 leading-relaxed max-w-xs mx-auto">
                    Merci {formData.name}. Notre équipe vérifie vos liens et vous contacte très vite sur WhatsApp pour activer votre espace affilié et votre compte Pro.
                  </p>

                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="w-full py-2.5 rounded-xl bg-slate-950 hover:bg-black text-white font-semibold text-xs transition-all cursor-pointer"
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
